// src/components/salesai.jsx
// "Sales AI" tab -- conversational interface to ask questions about the CRM
// data. Sends a compact summary of companies/deals/activities to Claude
// alongside the question.
//
// ARCHITECTURE (Path A -- pre-loaded context):
//   - Build a compact text block from `visibleCompanies` (which already
//     respects seeAll, allowedTypes, and allowedPipelines from App.jsx).
//   - Send: system prompt + data block + chat history + new question -> Claude
//   - Display the response in a chat bubble below.
//
// COST GUARD:
//   - Per-rep daily query cap (default 50/day) tracked in localStorage.
//     Resets at midnight local time. Admin bypasses the cap.
//   - Open question can run 80-150K tokens through Claude per call --
//     ~$0.30-$0.50 each. Cap prevents runaway costs.
//
// LIMITATIONS (v1):
//   - Text-only output. AI can name a company but cannot link to it.
//   - History clears on page reload (in-memory only).
//   - No streaming -- response arrives all at once.

import { useState, useRef, useEffect } from "react";
import { aiCall } from "../helpers.js";
import { Btn, Spin } from "./ui.jsx";

const DAILY_CAP = 50;             // questions per rep per day (admin bypasses)
const MAX_HISTORY_TURNS = 8;       // last 8 exchanges kept; older trimmed to control cost

// Compact textual summary of a company. Keep this LEAN -- this string gets
// repeated for every company, multiplying token cost.
function summarizeCompany(co) {
  const p = co.properties || {};
  const loc = [p.city, p.state].filter(Boolean).join(", ") || "(no location)";
  const deals = (co.deals || []).filter(d => d && d.name).map(d => {
    const amt = d.amount ? "$" + Number(d.amount).toLocaleString() : "no amount";
    return d.name + " [" + (d.stageName || d.stage || "?") + ", " + amt + "]";
  });
  const ct = (co.contacts || []).filter(c => c && c.name).map(c => c.name + (c.role ? " (" + c.role + ")" : ""));
  const parts = [
    "- " + p.name + " (id:" + co.id + ", " + loc + ", type:" + (p.type || "?") + ")",
  ];
  if (deals.length) parts.push("  Deals: " + deals.join("; "));
  if (ct.length)    parts.push("  Contacts: " + ct.slice(0, 5).join("; ") + (ct.length > 5 ? ` (+${ct.length - 5} more)` : ""));
  return parts.join("\n");
}

// Build the data context block. Caps total companies at maxCompanies (default
// 500) to keep tokens reasonable. If user has more, we sample and tell the
// AI -- a smarter v2 would filter based on question keywords first.
function buildCrmContext(companies, maxCompanies = 500) {
  const total = companies.length;
  const sampled = total > maxCompanies ? companies.slice(0, maxCompanies) : companies;
  const lines = sampled.map(summarizeCompany);
  let header = "DATA: " + total + " companies you have access to.";
  if (total > maxCompanies) {
    header += " (Showing first " + maxCompanies + " due to size; tell the user if you can't see all of them.)";
  }
  return header + "\n\n" + lines.join("\n");
}

// Per-user, per-day cap on AI queries. Stored locally so it resets cleanly
// when localStorage is cleared or on a new device (worst case rep gets 100
// queries/day across two devices -- acceptable).
function queryCountKey(userId) {
  const today = new Date().toISOString().slice(0, 10);  // YYYY-MM-DD
  return "crm_salesai_count_" + userId + "_" + today;
}
function getQueryCount(userId) {
  if (!userId) return 0;
  return Number(localStorage.getItem(queryCountKey(userId)) || "0");
}
function bumpQueryCount(userId) {
  if (!userId) return;
  const k = queryCountKey(userId);
  localStorage.setItem(k, String(getQueryCount(userId) + 1));
}

export function SalesAI({companies, currentUser}) {
  const [input,   setInput]   = useState("");
  const [history, setHistory] = useState([]);  // [{role: "user"|"assistant", text}]
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const scrollRef = useRef(null);

  // Whenever history changes, scroll to the bottom so latest message visible
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading]);

  const isAdmin = !!currentUser?.isAdmin;
  const queryCount = getQueryCount(currentUser?.id);
  const overCap = !isAdmin && queryCount >= DAILY_CAP;

  const ask = async () => {
    const q = input.trim();
    if (!q || loading) return;
    if (overCap) {
      setError("You've hit the daily limit of " + DAILY_CAP + " questions. Resets at midnight.");
      return;
    }
    setError("");
    // Optimistic UI: show the user's question immediately, then loading,
    // then assistant reply.
    setHistory(h => [...h, { role: "user", text: q }]);
    setInput("");
    setLoading(true);

    try {
      const context = buildCrmContext(companies);
      // Cap conversation history sent to AI -- older turns are trimmed to
      // control cost. Each turn is roughly 200-500 tokens of overhead.
      const recentTurns = [...history, { role: "user", text: q }].slice(-MAX_HISTORY_TURNS * 2);

      // Build the prompt. System tells the AI its role, then we paste the
      // data block, then the chat transcript ending with the new question.
      const prompt =
        "You are Sales AI, an assistant for ABM International sales reps. " +
        "Answer questions about the CRM data shown below. Be concise -- 1-3 sentences " +
        "unless the user asks for a list or detail. When listing companies or deals, " +
        "include the dollar amount and stage. If the data doesn't contain the answer, " +
        "say so directly rather than guessing.\n\n" +
        context + "\n\n" +
        "Conversation so far:\n" +
        recentTurns.map(t => (t.role === "user" ? "USER" : "ASSISTANT") + ": " + t.text).join("\n");

      const resp = await aiCall(prompt);
      const text = resp?.content?.find(b => b.type === "text")?.text || "(no response)";
      setHistory(h => [...h, { role: "assistant", text: text.trim() }]);
      bumpQueryCount(currentUser?.id);
    } catch (e) {
      setHistory(h => [...h, { role: "assistant", text: "Error: " + e.message }]);
    }
    setLoading(false);
  };

  const clearChat = () => {
    setHistory([]);
    setError("");
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl + Enter sends; plain Enter inserts newline so reps can write
    // longer questions without accidentally sending.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      ask();
    }
  };

  return (
    <div style={{animation:"fadeUp .35s ease both",display:"flex",flexDirection:"column",height:"calc(100vh - 100px)"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div>
          <h1 style={{fontSize:24,color:"var(--dark)",fontFamily:"Barlow Condensed,sans-serif"}}>✨ Sales AI</h1>
          <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>
            Ask questions about your {companies.length} companies and deals.
            {!isAdmin && (
              <span> · <strong>{queryCount}/{DAILY_CAP}</strong> questions used today</span>
            )}
          </div>
        </div>
        {history.length > 0 && (
          <Btn variant="ghost" onClick={clearChat}>Clear chat</Btn>
        )}
      </div>

      {/* Chat history scroll area */}
      <div ref={scrollRef}
        style={{flex:1,overflowY:"auto",background:"#fff",borderRadius:14,padding:18,border:"1px solid var(--warm2)",marginBottom:12,boxShadow:"var(--sh)"}}>
        {history.length === 0 && !loading && (
          <div style={{padding:40,textAlign:"center",color:"var(--muted)",fontSize:13}}>
            <div style={{fontSize:42,marginBottom:14,opacity:.4}}>✨</div>
            <div style={{fontWeight:600,marginBottom:14,color:"var(--brown)"}}>What would you like to know?</div>
            <div style={{maxWidth:480,margin:"0 auto",lineHeight:1.7}}>
              Try asking:<br/>
              <em>"Which companies are in 'Quote Sent' stage over $300,000?"</em><br/>
              <em>"How many deals do I have closing this month?"</em><br/>
              <em>"List my top 5 deals by value."</em><br/>
              <em>"Which companies haven't been contacted in 90 days?"</em>
            </div>
          </div>
        )}
        {history.map((turn, i) => (
          <div key={i} style={{display:"flex",justifyContent:turn.role === "user" ? "flex-end" : "flex-start",marginBottom:12}}>
            <div style={{
              maxWidth:"75%",
              padding:"12px 16px",
              borderRadius:turn.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              background: turn.role === "user" ? "var(--brown)" : "var(--warm1)",
              color: turn.role === "user" ? "#fff" : "var(--text)",
              fontSize:14,
              lineHeight:1.5,
              whiteSpace:"pre-wrap",
              wordBreak:"break-word",
            }}>
              {turn.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{display:"flex",justifyContent:"flex-start",marginBottom:12}}>
            <div style={{padding:"12px 16px",borderRadius:"16px 16px 16px 4px",background:"var(--warm1)",fontSize:13,color:"var(--muted)",display:"flex",alignItems:"center",gap:8}}>
              <Spin/> Thinking...
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13,marginBottom:10}}>
          {error}
        </div>
      )}

      {/* Input row */}
      <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={overCap ? "Daily limit reached -- resets at midnight" : "Ask anything about your CRM data... (Cmd/Ctrl+Enter to send)"}
          disabled={loading || overCap}
          rows={2}
          style={{
            flex:1,
            padding:"12px 14px",
            borderRadius:12,
            border:"1.5px solid var(--warm2)",
            background:"#fff",
            fontSize:14,
            outline:"none",
            fontFamily:"Barlow,sans-serif",
            resize:"vertical",
            minHeight:48,
            maxHeight:160,
          }}
        />
        <Btn onClick={ask} disabled={loading || !input.trim() || overCap}>
          {loading ? <Spin/> : "Ask"}
        </Btn>
      </div>
    </div>
  );
}
